import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type {
  ClientLotCommunityPlan,
  ClientLotCommunityPlanHydrated,
} from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientMasterPlan, ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { PlanType } from "api-types/modelTypes";
import type { UserNotificationType } from "common/types/userNotification";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import styles from "lib/components/PortalModal.module.scss";
import { EXTERNAL_ID_OVERVIEW_MESSAGE } from "lib/constants/messages";
import { PLAN_NAME_LENGTH_MAX } from "lib/house/planName";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { putLocationPlan } from "lib/storage/locationPlanStorage";
import { updateLotCommunityPlan } from "lib/storage/lotStorage";
import { putMasterPlan } from "lib/storage/masterPlanStorage";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";
import Checkbox from "ui/forms/CheckboxOld";
import Input from "ui/forms/Input";
import InputGroupNumber from "ui/forms/InputGroupNumber";
import InputGroupText from "ui/forms/InputGroupText";
import InputLabel from "ui/forms/InputLabel";
import useAppContext from "ui/hooks/useAppContext";
import Tooltip from "ui/popovers/Tooltip";

type EditablePlanProps = Pick<ClientLocationPlan, "externalId" | "basePrice" | "planDownloadEnabled"> & {
  disableNewOptions?: boolean;
  name?: string;
};

export type MasterCommunityOrLotPlan = ClientMasterPlan | ClientLocationPlan | ClientLotCommunityPlan;

interface EditPlanModalProps extends Omit<ModalProps, "children"> {
  planType: PlanType;
  plan: ClientMasterPlanHydrated | ClientLocationPlan | ClientLotCommunityPlan;
  onSubmit?: ((plan: MasterCommunityOrLotPlan) => void) | null;
}

/**
 * Lot community plans don't have the planDownloadEnabled boolean, so we need to circumvent that field for them
 */
function getPlanDownloadEnabled(plan: ClientMasterPlanHydrated | ClientLocationPlan | ClientLotCommunityPlan): boolean {
  return (plan as ClientMasterPlanHydrated | ClientLocationPlan).planDownloadEnabled || false;
}

function getDisabledNewOptions(
  plan: ClientMasterPlanHydrated | ClientLocationPlan | ClientLotCommunityPlan,
  planType: PlanType,
): boolean | undefined {
  if (planType === "community_plan") {
    return (plan as ClientLocationPlan).disableNewOptions;
  }
  return undefined;
}

function buildProps(
  plan: ClientMasterPlanHydrated | ClientLocationPlan | ClientLotCommunityPlan,
  planType: PlanType,
): EditablePlanProps {
  return {
    name: plan.name ?? undefined,
    externalId: plan.externalId,
    basePrice: plan.basePrice,
    planDownloadEnabled: getPlanDownloadEnabled(plan),
    disableNewOptions: getDisabledNewOptions(plan, planType),
  };
}

export default function EditPlanModal({ open, onClose, title, plan, planType, onSubmit }: EditPlanModalProps) {
  const { builderId, locationId, lotId } = useAppRouterParams();
  const { hasPermission } = useUserContext();
  const { setUserNotification } = useAppContext();
  const [initialValues, setInitialValues] = useState<EditablePlanProps>(buildProps(plan, planType));
  const [updateProps, setUpdateProps] = useState<EditablePlanProps>(initialValues);
  const [nameInputState, setNameInputState] = useState("");

  const [canUpdatePlan, setCanUpdatePlan] = useState(false);

  useEffect(() => {
    setInitialValues(buildProps(plan, planType));
    switch (planType) {
      case "studio_doc": {
        setCanUpdatePlan(hasPermission("EDIT_STUDIO_FILES"));
        break;
      }
      case "master_plan": {
        setCanUpdatePlan(hasPermission("EDIT_MASTER_PLANS"));
        break;
      }
      case "community_plan": {
        setCanUpdatePlan(hasPermission("EDIT_COMMUNITY_PLANS", { resourceId: plan.id }));
        break;
      }
      case "lot": {
        setCanUpdatePlan(hasPermission("EDIT_LOTS"));
        break;
      }
      case "home_plan": {
        setCanUpdatePlan(hasPermission("EDIT_HOME_PLANS"));
        break;
      }
    }
  }, [open]);

  const handleSubmit = async () => {
    let res: ClientMasterPlan | ClientLocationPlan | ClientLotCommunityPlanHydrated | undefined;
    let err: UserNotificationType | undefined;

    assertIsNotNullOrUndefined(builderId);
    assertIsNotNullOrUndefined(plan);

    switch (planType) {
      case "master_plan": {
        [res, err] = await putMasterPlan(builderId, plan.id, {
          ...updateProps,
          basePrice: updateProps.basePrice ?? undefined,
        });
        break;
      }
      case "community_plan": {
        assertIsNotNullOrUndefined(locationId);
        [res, err] = await putLocationPlan(builderId, locationId, plan.id, updateProps);
        break;
      }
      case "lot": {
        assertIsNotNullOrUndefined(locationId);
        assertIsNotNullOrUndefined(lotId);
        [res, err] = await updateLotCommunityPlan({
          builderId,
          locationId,
          lotId,
          lotCommunityPlanId: plan.id,
          lotCommunityPlanName: updateProps.name,
          basePrice: updateProps.basePrice ?? undefined,
          externalId: updateProps.externalId ?? undefined,
        });
        break;
      }
      default:
        throw new Error("Plan type not supported");
    }

    if (err) {
      setUserNotification(err);
      return;
    }

    setInitialValues(updateProps);
    if (onSubmit) {
      assertIsNotUndefined(res);
      onSubmit(res);
    }
    onClose?.();
  };

  function handlePropChange<T extends keyof EditablePlanProps>(
    value: EditablePlanProps[T],
    field: keyof EditablePlanProps,
  ) {
    setUpdateProps((prev) => ({ ...prev, [field]: value }));
  }

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;

    if (!inputValue) {
      setNameInputState("empty");
    } else {
      setNameInputState("valid");
    }
    handlePropChange(inputValue, "name");
  };

  const submittable = () => {
    const atLeastOneUpdate = Object.entries(updateProps).some(([field, value]) => {
      // if a string type field is empty and it had a falsy initial value, do not consider it an update
      if (typeof value === "string" && !value.trim() && !initialValues[field]) {
        return false;
      }
      return updateProps[field] !== initialValues[field];
    });
    const nameIsValid = updateProps.name === undefined || !!updateProps.name;
    const basePriceIsValid =
      planType !== "master_plan" || (updateProps.basePrice !== undefined && updateProps.basePrice !== null);
    return atLeastOneUpdate && nameIsValid && basePriceIsValid;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="small"
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          <Button btnSize="large" type="button" btnStyle="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button btnSize="large" type="submit" disabled={!submittable()} form="edit-plan-modal">
            Submit
          </Button>
        </>
      }
    >
      <form
        id="edit-plan-modal"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div className={styles.fieldWrapper}>
          <InputLabel text="Plan Name" />
          <Input
            value={updateProps.name}
            type="text"
            maxLength={PLAN_NAME_LENGTH_MAX}
            placeholder="Enter Plan Name"
            onChange={handleNameChange}
            className={nameInputState}
          />
        </div>
        <div className={styles.fieldWrapper}>
          <InputGroupText
            tooltip={EXTERNAL_ID_OVERVIEW_MESSAGE}
            type="text"
            placeholder="Enter External ID"
            onChange={(externalId) => handlePropChange(externalId, "externalId")}
            inputValue={updateProps.externalId}
            labelText="External ID"
          />
        </div>
        <div className={styles.fieldWrapper}>
          <InputGroupNumber
            labelText="Base Price"
            inputValue={updateProps.basePrice ?? undefined}
            onChange={(basePrice) => handlePropChange(basePrice, "basePrice")}
          />
        </div>
        {canUpdatePlan && (
          <div className={styles.checkboxes}>
            {(planType === "master_plan" || planType === "community_plan") && (
              <Checkbox
                className={styles.checkbox}
                checked={updateProps.planDownloadEnabled}
                onChange={(e) => handlePropChange(e.target.checked, "planDownloadEnabled")}
                label="Plan Download Enabled"
              />
            )}
            {planType === "community_plan" && (
              <Tooltip.Root delayDuration={500}>
                <Tooltip.Trigger>
                  <div>
                    <Checkbox
                      className={styles.checkbox}
                      checked={updateProps.disableNewOptions}
                      onChange={(e) => handlePropChange(e.target.checked, "disableNewOptions")}
                      label="Disable New Options"
                    />
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <>
                    When checked, new options published from Studio <br />
                    files will be disabled in this community by default. <br />
                    These options can be re-enabled in Plan Library.
                  </>
                </Tooltip.Content>
              </Tooltip.Root>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}
